import { ref } from 'vue';
import { useRuntimeConfig } from '#imports';
import { useCurrentUser } from '@/composables/use-auth';
import { formatPersonName } from '@/utils/sdk-utils';

export function initializeJitsi (opts) {
  const domain = opts?.domain || 'meet.jit.si';
  const scriptSrc = `https://${domain}/external_api.js`;

  return new Promise((resolve, reject) => {
    if (initializeJitsi._loaded) return resolve();
    
    try {
      const d = globalThis.document;
      const scriptEl = d.createElement('script');
      scriptEl.src = scriptSrc;
      scriptEl.async = 1;
      
      scriptEl.addEventListener('load', () => {
        initializeJitsi._loaded = true;
        console.log(`Jitsi embed loaded from ${scriptSrc}`);
        return resolve();
      });
      
      scriptEl.addEventListener('error', (error) => {
        console.error(`Failed to load Jitsi script from ${scriptSrc}:`, error);
        return resolve(); // Resolve instead of reject to prevent app from breaking
      });
      
      d.querySelector('head').appendChild(scriptEl);
    } catch (error) {
      console.error('Error initializing Jitsi script:', error);
      return resolve(); // Resolve to prevent app from breaking
    }
  });
}

export function getSdk () {
  if (!globalThis.window?.JitsiMeetExternalAPI) {
    console.error('JitsiMeetExternalAPI is not available');
    return null;
  }
  return globalThis.window.JitsiMeetExternalAPI;
}

export function useJitsi (opts) {
  const runtimeConfig = useRuntimeConfig();
  console.warn('runtimeConfig', runtimeConfig)
  opts = Object.assign({
    domain: runtimeConfig.public?.jitsiDomain || 'meet.jit.si',
    selector: '#jitsi-mount',
  }, opts);

  const currentUser = useCurrentUser();

  const api = ref(null);

  const endJitsiCall = async () => {
    if (!api.value) return;
    api.value.dispose();
    api.value = null;
  };

  const startJitsiCall = async (roomName, copts) => {
    if (!roomName) throw new Error('roomName is required');
    copts = Object.assign({
      appName: 'Parmazip',
      watermarkLink: 'https://parmazip.ca',
    }, copts);
    copts.selector ??= opts.selector;
    copts.domain ??= opts.domain;
    if (!copts.mountPoint) {
      copts.mountPoint = globalThis.document.querySelector(copts.selector);
    }
    await endJitsiCall();
    const callopts = { 
      roomName,
      parentNode: copts.mountPoint,
      configOverwrite: {
        disableModeratorIndicator: true,
        toolbarButtons: [
          'microphone', 
          'camera', 
          'chat',
          'tileview',
          'hangup',
        ],
        enableNoisyMicDetection: true,
        disablePolls: true,
        startWithVideoMuted: false,
        startWithAudioMuted: true,
        localSubject: 'Parmazip Telepharmacy',
        defaultLocalDisplayName: currentUser.value?.uid ? formatPersonName(currentUser.value.name) : 'Me',
        openBridgeChannel: 'websocket',
        channelLastN: -1,
        lastNLimits: {
          5: 20,
          30: 15,
          50: 10,
          70: 5,
          90: 2
        },
        disableSimulcast: false,
        enableLayerSuspension: true,
        resolution: 720,
        constraints: {
          video: {
            height: {
              ideal: 720,
              max: 720,
              min: 240
            }
          }
        },
      },
      interfaceConfigOverwrite: {
        APP_NAME: copts.appName,
        DISPLAY_WELCOME_PAGE_CONTENT: true,
        JITSI_WATERMARK_LINK: copts.watermarkLink,
        SETTINGS_SECTIONS: [],
        TOOLBAR_BUTTONS: [
          'microphone', 
          'camera', 
          'closedcaptions', 
          'desktop', 
          'fullscreen',
          'chat',
          'tileview',
          'hangup',
          // 'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
          // 'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
          // 'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
          // 'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
          // 'e2ee',
        ],
        FILM_STRIP_MAX_HEIGHT: 120,
        TILE_VIEW_MAX_COLUMNS: 2,
        DEFAULT_BACKGROUND: '#474747',
        DEFAULT_LOCAL_DISPLAY_NAME: 'Me',
        DEFAULT_REMOTE_DISPLAY_NAME: 'Me',
        DISABLE_DOMINANT_SPEAKER_INDICATOR: true,
        DISABLE_FOCUS_INDICATOR: true,
        FILMSTRIP_DISPLAY_DURATION: 1000,
        INITIAL_TOOLBAR_TIMEOUT: 20000,
        TOOLBAR_TIMEOUT: 4000,
        TOOLBAR_ALWAYS_VISIBLE: false,
        VIDEO_LAYOUT_FIT: 'both',
        VERTICAL_FILMSTRIP: false,
        filmStripOnly: false,
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
      },
    };
    if (currentUser.value?.uid) {
      callopts.userInfo = {
        displayName: formatPersonName(currentUser.value.name),
        email: currentUser.value.email,
      };
    }
    console.debug('Jitsi call options', copts.domain, callopts);
    const Sdk = getSdk();
    api.value = new Sdk(copts.domain, callopts);
    if (currentUser.value?.picURL) {
      api.value.executeCommand('avatarUrl', currentUser.value.picURL);
    }
    api.value.executeCommand('toggleAudio');
    api.value.executeCommand('localSubject', 'Parmazip Telepharmacy');
    
    
    // Set tile view as default layout
    api.value.executeCommand('setTileView', true);

    // Add event listener to ensure tile view is maintained when participants join
    api.value.addListener('participantJoined', () => {
      setTimeout(() => {
        api.value.executeCommand('setTileView', true);
        api.value.executeCommand('localSubject', 'Parmazip Telepharmacy');
      }, 500);
    });

    // Also set tile view when video conference is joined
    api.value.addListener('videoConferenceJoined', () => {
      setTimeout(() => {
        api.value.executeCommand('setTileView', true);
        api.value.executeCommand('localSubject', 'Parmazip Telepharmacy');
      }, 500);
    });


    // handle events
    const events = copts.events || {};
    for (const [event, handler] of Object.entries(events)) {
      api.value.addListener(event, handler);
    }
  };

  return {
    api,
    startJitsiCall,
    endJitsiCall,
  };
}
